/**
 * Moteur de correspondance de la recherche globale : normalisation bilingue, score, surlignage.
 *
 * <p>Fonctions pures, sans Angular : la même règle doit valoir pour les rubriques (filtrées ici, dans
 * le navigateur) et, plus tard, pour le surlignage des résultats servis par `/api/recherche`.
 *
 * <p><b>Pourquoi une normalisation arabe dédiée.</b> Un contrôleur qui tape « اعلام » doit trouver
 * « مذكرات الإعلام » : la hamza, la tâ marbûta, les voyelles brèves et le tatweel sont des variantes
 * d'écriture, pas des différences de sens. Sans cela, la recherche ne répond qu'à qui recopie le
 * libellé à la lettre près — c'est-à-dire à personne.
 */

/** Un texte normalisé, avec pour chaque caractère sa position dans le texte d'origine. */
export interface TexteNormalise {
  readonly texte: string;
  /** `origine[i]` = index, dans le texte source, du caractère qui a produit `texte[i]`. */
  readonly origine: readonly number[];
}

/** Un morceau de libellé à afficher, surligné ou non. */
export interface Segment {
  readonly texte: string;
  readonly surligne: boolean;
}

/** Voyelles brèves, tanwîn, shadda, sukûn, alif suscrit, et le tatweel (ـ). */
const DIACRITIQUES_ARABES = /[ؐ-ًؚ-ٰٟۖ-ۭـ]/;
/** Accents latins, une fois le caractère décomposé (NFD). */
const DIACRITIQUES_LATINS = /[̀-ͯ]/g;

const EQUIVALENCES_ARABES: Readonly<Record<string, string>> = {
  'أ': 'ا', 'إ': 'ا', 'آ': 'ا', 'ٱ': 'ا',
  'ة': 'ه',
  'ى': 'ي', 'ئ': 'ي',
  'ؤ': 'و',
};

/**
 * Normalise pour comparer : minuscules, sans accents, variantes arabes ramenées à une forme.
 * Garde la trace des positions d'origine, indispensable pour surligner le libellé NON normalisé.
 */
export function normaliser(source: string): TexteNormalise {
  let texte = '';
  const origine: number[] = [];
  for (let i = 0; i < source.length; i++) {
    const c = source[i];
    if (DIACRITIQUES_ARABES.test(c)) continue;
    const forme = (EQUIVALENCES_ARABES[c] ?? c)
      .normalize('NFD')
      .replace(DIACRITIQUES_LATINS, '')
      .toLowerCase()
      .replace('œ', 'oe')
      .replace('æ', 'ae');
    for (const f of forme) {
      texte += f;
      origine.push(i);
    }
  }
  return { texte, origine };
}

/** Découpe une requête en mots normalisés (tous doivent correspondre : « ET » implicite). */
export function motsDeRequete(requete: string): string[] {
  return normaliser(requete.trim()).texte.split(/\s+/).filter(Boolean);
}

/** Début de mot : début de chaîne, ou précédé d'un séparateur, ou de l'article « ال ». */
function estDebutDeMot(texte: string, i: number): boolean {
  if (i === 0) return true;
  if (/[\s'’\-_/()«»،.]/.test(texte[i - 1])) return true;
  // « اعلام » doit trouver « الإعلام » en DÉBUT de mot, pas en simple inclusion.
  return i >= 2 && texte.slice(i - 2, i) === 'ال' && estDebutDeMot(texte, i - 2);
}

/**
 * Score d'un texte pour une requête ; 0 = pas de correspondance.
 *
 * <p>Barème : égalité > préfixe du texte > début d'un mot > inclusion > lettres dans l'ordre (tolère
 * une frappe sautée : « tabdb » → « Tableau de bord »). Chaque mot de la requête doit être trouvé.
 */
export function scorer(cible: TexteNormalise, mots: readonly string[]): { score: number; plages: [number, number][] } {
  if (!mots.length) return { score: 0, plages: [] };
  const t = cible.texte;
  let score = 0;
  const plages: [number, number][] = [];
  for (const mot of mots) {
    if (t === mot) {
      score += 100;
      plages.push([0, t.length]);
      continue;
    }
    let meilleur = 0;
    let plage: [number, number] | null = null;
    for (let i = t.indexOf(mot); i !== -1; i = t.indexOf(mot, i + 1)) {
      const valeur = i === 0 ? 40 : estDebutDeMot(t, i) ? 25 : 10;
      if (valeur > meilleur) {
        meilleur = valeur;
        plage = [i, i + mot.length];
      }
    }
    if (!plage && mot.length >= 3) {
      const positions = sousSequence(t, mot);
      if (positions) {
        meilleur = 3;
        for (const p of positions) plages.push([p, p + 1]);
      }
    }
    if (!meilleur) return { score: 0, plages: [] };
    score += meilleur;
    if (plage) plages.push(plage);
  }
  // À score égal, le libellé le plus court est le plus proche de ce qu'on a tapé.
  return { score: score - t.length / 1000, plages };
}

/** Positions des lettres de `mot` retrouvées dans l'ordre dans `texte`, ou `null`. */
function sousSequence(texte: string, mot: string): number[] | null {
  const positions: number[] = [];
  let j = 0;
  for (let i = 0; i < texte.length && j < mot.length; i++) {
    if (texte[i] === mot[j]) {
      positions.push(i);
      j++;
    }
  }
  return j === mot.length ? positions : null;
}

/** Découpe le libellé d'ORIGINE en segments surlignés d'après des plages du texte normalisé. */
export function segmenter(source: string, cible: TexteNormalise, plages: readonly [number, number][]): Segment[] {
  if (!plages.length) return [{ texte: source, surligne: false }];
  const marque = new Array<boolean>(source.length).fill(false);
  for (const [debut, fin] of plages) {
    if (fin <= debut) continue;
    const de = cible.origine[debut];
    const a = cible.origine[fin - 1];
    for (let i = de; i <= a; i++) marque[i] = true;
  }
  const segments: Segment[] = [];
  for (let i = 0; i < source.length; ) {
    const surligne = marque[i];
    let j = i;
    while (j < source.length && marque[j] === surligne) j++;
    segments.push({ texte: source.slice(i, j), surligne });
    i = j;
  }
  return segments;
}
